import { useEffect, useState, type FormEvent } from "react";
import { Field, Input } from "@/components/base/FormControls";
import DocumentIdentityCard from "@/components/feature/DocumentIdentityCard";
import { describeSupabaseError } from "@/store/shared/dataErrors";
import { buildWhatsappLink, nettoyerNumeroWhatsapp } from "@/utils/whatsapp";
import { useParametres } from "../ParametresProvider";
import type { Profil } from "../types";

export default function ProfilSection() {
  const { profil, updateProfil } = useParametres();
  const [form, setForm] = useState<Profil>(profil);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Reflète le profil réel (chargé depuis la base) dans le formulaire.
  useEffect(() => {
    setForm(profil);
  }, [profil]);

  const update = (key: keyof Profil, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
    setSaveError(null);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors: Record<string, string> = {};
    if (!form.prenom.trim()) nextErrors.prenom = "Le prénom est obligatoire.";
    if (!form.nom.trim()) nextErrors.nom = "Le nom est obligatoire.";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setSaving(true);
    setSaveError(null);
    try {
      await updateProfil({
        prenom: form.prenom.trim(),
        nom: form.nom.trim(),
        email: form.email,
        telephone: form.telephone.trim(),
        whatsapp: nettoyerNumeroWhatsapp(form.whatsapp),
        logo: form.logo,
        logoPath: form.logoPath,
        adresse: form.adresse.trim(),
        siteWeb: form.siteWeb.trim(),
        ifu: form.ifu.trim(),
        rccm: form.rccm.trim(),
      });
      setSaved(true);
    } catch (caught) {
      setSaveError(describeSupabaseError(caught));
    } finally {
      setSaving(false);
    }
  };

  const whatsappLink = buildWhatsappLink(form.whatsapp);

  return (
    <section className="flex flex-col rounded-lg border border-background-200 bg-background-50">
      <header className="flex items-center gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-600">
          <i className="ri-user-line text-base" aria-hidden="true" />
        </span>
        <div>
          <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">Profil</h3>
          <p className="text-xs text-foreground-500">Vos informations personnelles visibles dans votre espace.</p>
        </div>
      </header>

      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-5 p-4 md:p-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Prénom" htmlFor="profil-prenom" required error={errors.prenom}>
            <Input
              id="profil-prenom"
              value={form.prenom}
              onChange={(event) => update("prenom", event.target.value)}
              placeholder="Votre prénom"
              invalid={Boolean(errors.prenom)}
              maxLength={60}
            />
          </Field>
          <Field label="Nom" htmlFor="profil-nom" required error={errors.nom}>
            <Input
              id="profil-nom"
              value={form.nom}
              onChange={(event) => update("nom", event.target.value)}
              placeholder="Votre nom"
              invalid={Boolean(errors.nom)}
              maxLength={60}
            />
          </Field>
          <Field
            label="Email"
            htmlFor="profil-email"
            hint="Adresse de connexion — contactez le support pour la modifier."
          >
            <Input id="profil-email" type="email" value={form.email} icon="ri-mail-line" readOnly disabled />
          </Field>
          <Field label="Téléphone" htmlFor="profil-telephone" hint="Format local, ex. 70 00 00 00.">
            <Input
              id="profil-telephone"
              type="tel"
              value={form.telephone}
              onChange={(event) => update("telephone", event.target.value)}
              placeholder="70 00 00 00"
              icon="ri-phone-line"
            />
          </Field>
          <Field
            label="Téléphone WhatsApp"
            htmlFor="profil-whatsapp"
            hint="Facultatif. Format international, ex. +226 70 00 00 00."
          >
            <Input
              id="profil-whatsapp"
              type="tel"
              value={form.whatsapp}
              onChange={(event) => update("whatsapp", event.target.value)}
              placeholder="+226 XX XX XX XX"
              icon="ri-whatsapp-line"
              maxLength={40}
            />
            {whatsappLink && (
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1.5 inline-flex w-fit items-center gap-1.5 whitespace-nowrap text-xs font-label font-medium text-secondary-700 transition-colors hover:text-secondary-900 cursor-pointer"
              >
                <i className="ri-whatsapp-line text-sm" aria-hidden="true" />
                Ouvrir WhatsApp
              </a>
            )}
          </Field>
          <Field
            label="Adresse"
            htmlFor="profil-adresse"
            hint="Affichée en pied de page des documents PDF."
            className="sm:col-span-2"
          >
            <Input
              id="profil-adresse"
              value={form.adresse}
              onChange={(event) => update("adresse", event.target.value)}
              placeholder="Ex. Secteur 15, Ouagadougou"
              icon="ri-map-pin-line"
              maxLength={160}
            />
          </Field>
        </div>

        <div className="border-t border-background-200 pt-5">
          <DocumentIdentityCard
            logo={form.logo}
            onLogoChange={(value) => {
              setForm((prev) => ({ ...prev, logo: value }));
              setSaved(false);
              setSaveError(null);
            }}
            nom={`${form.prenom} ${form.nom}`.trim()}
            adresse={form.adresse}
            telephone={form.telephone}
            email={form.email}
            siteWeb={form.siteWeb}
            onSiteWebChange={(value) => update("siteWeb", value)}
            ifu={form.ifu}
            onIfuChange={(value) => update("ifu", value)}
            rccm={form.rccm}
            onRccmChange={(value) => update("rccm", value)}
            logoLabel="Logo"
          />
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-background-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
          {saveError ? (
            <p className="flex items-center gap-1.5 text-xs text-primary-700">
              <i className="ri-error-warning-line text-sm" aria-hidden="true" />
              L'enregistrement a échoué. Réessayez.
            </p>
          ) : saved ? (
            <p className="flex items-center gap-1.5 text-xs text-secondary-700">
              <i className="ri-checkbox-circle-line text-sm" aria-hidden="true" />
              Profil enregistré.
            </p>
          ) : (
            <span className="text-xs text-foreground-400">Vos informations sont enregistrées dans votre compte.</span>
          )}
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
          >
            <i className={saving ? "ri-loader-4-line animate-spin text-base" : "ri-save-line text-base"} aria-hidden="true" />
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </form>
    </section>
  );
}